import ErrorComponent from "@/components/atoms/ErrorComponent";
import CompanyHeader from "@/components/molecules/card-headers/companies/CompanyHeader";
import RecordCallLogsCard from "@/components/molecules/cards/callLogs/RecordCallLogsCard";
import CompanyCoursesCard from "@/components/molecules/cards/companies/CompanyCoursesCard";
import CompanyDiagnosticsCard from "@/components/molecules/cards/companies/CompanyDiagnosticsCard";
import CompanyDetailsCard from "@/components/molecules/cards/companies/CompanyDetailsCard";
import CompanyFilesCard from "@/components/molecules/cards/companies/CompanyFilesCard";
import CompanyMailsCard from "@/components/molecules/cards/companies/CompanyMailsCard";
import CompanyMeetingsCard from "@/components/molecules/cards/companies/CompanyMeetingsCard";
import CompanyNotesCard from "@/components/molecules/cards/companies/CompanyNotesCard";
import CompanyUsersCard from "@/components/molecules/cards/companies/CompanyUsersCard";
import CompanyPeopleCard from "@/components/molecules/cards/person/CompanyPeopleCard";
import StoreCompanyNoteModal from "@/components/molecules/modals/company/StoreCompanyNoteModal";
import { UUID_REGEX } from "@/constants/regexs";
import { defaultCompanyTab } from "@/helpers/CompanyHelpers";
import { canSeeMontageMeetings } from "@/helpers/MeetingHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { pluralize } from "@/helpers/utils";
import ICompany from "@/interfaces/Company";
import { useSetPresenceActivity } from "@/contexts/PresenceContext";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faCalendar,
  faCalendarCheck,
  faComment,
  faFile,
  faFileLines,
  faMailBulk,
  faPhone,
  faSchool,
  faTicket,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ElementRef, FC, useRef, useState } from "react";
import { Card, Col, Nav, Row, Tab } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import SimpleBar from "simplebar-react";

const CompanyView: FC = () => {
  const { id } = useParams<{ id: string }>();
  // L'onglet vit dans l'URL : c'est ce qui permet à la recherche globale
  // d'ouvrir une note ou un rendez-vous au bon endroit, et accessoirement de
  // partager un lien vers un onglet précis.
  const [searchParams, setSearchParams] = useSearchParams();
  const StoreCompanyNoteModalRef =
    useRef<ElementRef<typeof StoreCompanyNoteModal>>(null);
  const user = useSelector(selectCurrentUser);
  // Un CSM entre par la formation, tout le monde d'autre par les notes.
  const defaultTab = defaultCompanyTab(user);
  const requestedTab = searchParams.get("tab") ?? defaultTab;
  const [company, setCompany] = useState<ICompany>();
  useSetPresenceActivity(company?.name ? `Entreprise ${company.name}` : null);
  const canSeeMontage = !!company && canSeeMontageMeetings(user, company);
  // Un lien partagé vers l'onglet montage doit retomber sur les rendez-vous
  // commerciaux chez qui ne peut pas le voir : sans ça la carte s'affiche vide,
  // aucun onglet actif.
  const activeTab =
    requestedTab === "montage-meetings" && !canSeeMontage
      ? "meetings"
      : requestedTab;
  const [totals, setTotals] = useState({
    employees: 0,
    companies: 0,
    courses: 0,
    people: 0,
    users: 0,
    commercialMeetings: 0,
    montageMeetings: 0,
    tickets: 0,
    files: 0,
    notes: 0,
    mails: 0,
    calls: 0,
  });

  // L'onglet d'entrée se lit en premier : quand c'est Note, elle passe devant
  // Formation au lieu de rester à sa place habituelle. Formation est déjà en
  // tête, il n'y a donc que ce cas-là à traiter.
  //
  // Un `order` CSS aurait suffi visuellement, mais il aurait laissé l'ordre du
  // DOM — donc la tabulation au clavier — sur l'ancien ordre.
  const notesFirst = defaultTab === "notes";
  const notesTab = (
    <Nav.Item>
      <Nav.Link
        eventKey="notes"
        className="d-flex align-items-center py-3 px-x1 mb-0"
      >
        <FontAwesomeIcon icon={faComment} className="text-600 tab-icon" />
        <h6 className="text-600 mb-0 ms-1">
          {pluralize("Note", totals.notes)}
          &nbsp;(
          {totals.notes})
        </h6>
      </Nav.Link>
    </Nav.Item>
  );

  const onGetTotal = (key: string, total: number) => {
    setTotals((prev) => ({ ...prev, [key]: total }));
  };

  const showOrHiddenNoteModal = (
    companyId: string,
    noteId?: string,
    message?: string
  ) =>
    StoreCompanyNoteModalRef.current?.showOrHiddenModal(
      companyId,
      noteId,
      message
    );

  if (!id || !UUID_REGEX.test(id)) {
    return (
      <ErrorComponent error={{ status: 404, data: {} }} absolute returnButton />
    );
  }

  return (
    <div className="formalys-page formalys-detail-page">
      {company && <CompanyHeader id={id} archived={company.archived} />}
      <StoreCompanyNoteModal ref={StoreCompanyNoteModalRef} />

      <Row className="g-3">
        <Col xxl={4} xl={5}>
          <CompanyDetailsCard
            id={id}
            card
            callBack={setCompany}
            showOrHiddenNoteModal={showOrHiddenNoteModal}
          />
        </Col>
        {company && (
          <Col xxl={8} xl={7}>
            <Card>
              <Tab.Container
                activeKey={activeTab}
                onSelect={(key) => {
                  if (!key) return;
                  // replace : changer d'onglet ne doit pas empiler une entrée
                  // d'historique par clic, sinon le bouton Retour devient
                  // inutilisable.
                  setSearchParams({ tab: key }, { replace: true });
                }}
              >
                <SimpleBar>
                  <Card.Header className="p-0 border-bottom">
                    <Nav className="nav-tabs border-0 flex-nowrap tab-contact-details">
                      {notesFirst && notesTab}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="courses"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faSchool}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Formation", totals.courses)}
                            &nbsp;(
                            {totals.courses})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      {/* Les conventions signées atterrissent ici, d'où le
                          libellé et la place juste après les formations. La
                          carte accepte n'importe quel document : `eventKey`
                          reste "files", les liens ?tab=files continuent donc
                          de marcher. Réservé aux mêmes rôles qu'avant. */}
                      {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                        <Nav.Item>
                          <Nav.Link
                            eventKey="files"
                            className="d-flex align-items-center py-3 px-x1 mb-0"
                          >
                            <FontAwesomeIcon
                              icon={faFile}
                              className="text-600 tab-icon"
                            />
                            <h6 className="text-600 mb-0 ms-1">
                              {pluralize("Convention", totals.files)}
                              &nbsp;({totals.files})
                            </h6>
                          </Nav.Link>
                        </Nav.Item>
                      )}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="people"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Salarié", totals.people)}
                            &nbsp;(
                            {totals.people})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      {!notesFirst && notesTab}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="meetings"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            RDV&nbsp;commercial&nbsp;(
                            {totals.commercialMeetings})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      {/* L'onglet montage n'est monté que pour ceux à qui
                          l'API renvoie ces rendez-vous : pour les autres il
                          resterait vide à vie. */}
                      {canSeeMontage && (
                        <Nav.Item>
                          <Nav.Link
                            eventKey="montage-meetings"
                            className="d-flex align-items-center py-3 px-x1 mb-0"
                          >
                            <FontAwesomeIcon
                              icon={faCalendarCheck}
                              className="text-600 tab-icon"
                            />
                            <h6 className="text-600 mb-0 ms-1">
                              RDV&nbsp;montage&nbsp;(
                              {totals.montageMeetings})
                            </h6>
                          </Nav.Link>
                        </Nav.Item>
                      )}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="diagnostics"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faFileLines}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">Diagnostics</h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="users"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            Commercials&nbsp;/&nbsp;Télépros&nbsp;(
                            {totals.users})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="mails"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faMailBulk}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Mail", totals.mails)}&nbsp;(
                            {totals.mails})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="calls"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faPhone}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Appel", totals.calls)}
                            &nbsp;(
                            {totals.calls})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Header>
                </SimpleBar>
                <Card.Body className="bg-light">
                  <Tab.Content>
                    <Tab.Pane eventKey="courses">
                      <CompanyCoursesCard
                        id={id}
                        company={company}
                        onGetTotal={(total) => onGetTotal("courses", total)}
                      />
                    </Tab.Pane>
                    {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                      <Tab.Pane eventKey="files">
                        <CompanyFilesCard
                          id={id}
                          onGetTotal={(total) => onGetTotal("files", total)}
                        />
                      </Tab.Pane>
                    )}
                    <Tab.Pane eventKey="people">
                      <CompanyPeopleCard
                        id={id}
                        onGetTotal={(total) => onGetTotal("people", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="notes">
                      <CompanyNotesCard
                        id={id}
                        onGetTotal={(total) => onGetTotal("notes", total)}
                        showOrHiddenNoteModal={showOrHiddenNoteModal}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="meetings">
                      <CompanyMeetingsCard
                        companyId={id}
                        calendarType="commercial"
                        onGetTotal={(total) =>
                          onGetTotal("commercialMeetings", total)
                        }
                      />
                    </Tab.Pane>
                    {canSeeMontage && (
                      <Tab.Pane eventKey="montage-meetings">
                        <CompanyMeetingsCard
                          companyId={id}
                          calendarType="montage"
                          onGetTotal={(total) =>
                            onGetTotal("montageMeetings", total)
                          }
                        />
                      </Tab.Pane>
                    )}
                    <Tab.Pane eventKey="diagnostics">
                      <CompanyDiagnosticsCard companyId={id} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="users">
                      <CompanyUsersCard
                        companyId={id}
                        onGetTotal={(total) => onGetTotal("users", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="mails">
                      <CompanyMailsCard
                        id={id}
                        onGetTotal={(total) => onGetTotal("mails", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="calls">
                      <RecordCallLogsCard
                        companyId={id}
                        onGetTotal={(total) => onGetTotal("calls", total)}
                      />
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Tab.Container>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CompanyView;
